import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { RETENTION_POLICIES } from '../api/types';
import type { Archive, RetentionPolicy } from '../api/types';
import { useAsync } from '../lib/hooks';
import { PageHeader } from '../components/PageHeader';
import { ApiState, EmptyState, PendingState } from '../components/States';
import { Button, Card, CardHead } from '../components/ui';
import { Field } from '../components/Field';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';
import { RetentionBadge } from './shared';
import { IconWarning } from '../components/Icons';

export function Retention() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const archives = useAsync(() => api.listArchives(200), []);
  const archiveId = params.get('archive') ?? '';

  return (
    <>
      <PageHeader title={t('retention.title')} subtitle={t('nav.retention')} />
      <ApiState {...archives} onRetry={archives.reload}>
        {(page) =>
          page.items.length === 0 ? (
            <Card>
              <EmptyState title={t('archives.empty')} />
            </Card>
          ) : (
            <RetentionEditor
              archives={page.items}
              initial={page.items.some((a) => a.id === archiveId) ? archiveId : page.items[0].id}
              onChanged={archives.reload}
            />
          )
        }
      </ApiState>
    </>
  );
}

function RetentionEditor({
  archives,
  initial,
  onChanged,
}: {
  archives: Archive[];
  initial: string;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [archiveId, setArchiveId] = useState(initial);
  const selected = archives.find((a) => a.id === archiveId) ?? archives[0];
  const [policy, setPolicy] = useState<RetentionPolicy>(selected.retentionPolicy);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    setPolicy(selected.retentionPolicy);
  }, [selected.id, selected.retentionPolicy]);

  const dirty = policy !== selected.retentionPolicy;

  const doChange = async () => {
    setBusy(true);
    try {
      await api.updateRetention(selected.id, policy);
      toast.success(t('retention.updated'));
      setConfirm(false);
      onChanged();
    } catch (err) {
      setConfirm(false);
      if (err instanceof ApiError && err.isNotImplemented) {
        setPending(true);
      } else {
        toast.error(err instanceof ApiError ? err.message : String(err));
      }
    } finally {
      setBusy(false);
    }
  };

  if (pending) {
    return (
      <Card>
        <PendingState />
      </Card>
    );
  }

  return (
    <Card>
      <CardHead title={t('retention.changeTitle')} />
      <div className="card__body">
        <div className="form-row" style={{ marginBottom: 20 }}>
          <Field label={t('retention.pickArchive')}>
            {(p) => (
              <select className="select" {...p} value={archiveId} onChange={(e) => setArchiveId(e.target.value)}>
                {archives.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <div className="field" style={{ marginBottom: 0 }}>
            <span className="field__label">{t('retention.current')}</span>
            <div style={{ paddingTop: 4 }}>
              <RetentionBadge policy={selected.retentionPolicy} />
            </div>
          </div>
        </div>

        <Field label={t('retention.label')}>
          {(p) => (
            <select className="select" {...p} value={policy} onChange={(e) => setPolicy(e.target.value as RetentionPolicy)}>
              {RETENTION_POLICIES.map((rp) => (
                <option key={rp} value={rp}>
                  {t(`retention.${rp}`)}
                </option>
              ))}
            </select>
          )}
        </Field>

        {dirty ? (
          <div className="alert alert--warning" role="note">
            <IconWarning width={18} height={18} />
            <span>{t('retention.warnBody')}</span>
          </div>
        ) : null}

        <Button variant="danger" onClick={() => setConfirm(true)} disabled={!dirty}>
          {t('retention.confirmChange')}
        </Button>
      </div>

      <ConfirmDialog
        open={confirm}
        danger
        busy={busy}
        title={t('retention.warnTitle')}
        body={t('retention.warnBody')}
        confirmLabel={t('retention.confirmChange')}
        onConfirm={doChange}
        onCancel={() => setConfirm(false)}
      />
    </Card>
  );
}
